import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { BreadcrumbNav } from "components/molecules";
import { Source, UserLevel } from "utils/constant";
import Wilayah from "services/Wilayah";
import Fasyankes from "services/Fasyankes";
import BKK from "services/BKK";
import Laporan from "services/Laporan";
import TableComponent from "./Table";
import FilterSection from "./Filter";
import LoadingSpinner from "components/LoadingSpinner";
import { getBkkUserValue, resolveLevelIdForApply } from "./filterUtils";

const WilayahService = new Wilayah();
const FasyankesService = new Fasyankes();
const BKKService = new BKK();
const LaporanService = new Laporan();

const DetailLaporan = () => {
  const module = "Mikroskopis Fasyankes / BKK";
  const { currentUser } = useSelector((state) => state.authReducer);
  const history = useHistory();
  const tableRef = useRef(null);

  const [reportData, setReportData] = useState([]);
  const [showFilter, setShowFilter] = useState(true);
  const [showData, setShowData] = useState(false);
  const [levelData, setLevelData] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState(currentUser.level);
  const [applySelectedLevel, setApplySelectedLevel] = useState(
    currentUser.level,
  );
  const [currentLevel, setCurrentLevel] = useState("");
  const [levelIdValue, setLevelIdValue] = useState("");
  const [levelDataOptions, setLevelDataOptions] = useState({
    provinces: [],
    districts: [],
    fasyankes: [],
    induks: [],
    wilkers: [],
  });
  const [selectedProvince, setSelectedProvince] = useState(
    currentUser?.province_id,
  );
  const [selectedDistrict, setSelectedDistrict] = useState(
    currentUser?.district_id,
  );
  const [selectedFasyankes, setSelectedFasyankes] = useState(
    currentUser?.fasyankes_id,
  );
  const [selectedInduk, setSelectedInduk] = useState("");
  const [selectedWilker, setSelectedWilker] = useState("");
  const [periodeLaporan, setPeriodeLaporan] = useState("");
  const [periodeLaporanValue, setPeriodeLaporanValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [reportInfo, setReportInfo] = useState({});

  const generateLevelOptions = (userLevel) => {
    switch (userLevel) {
      case UserLevel.BKKWilker:
        return [{ label: "BKK Wilker", value: UserLevel.BKKWilker }];
      case UserLevel.BKKInduk:
        return [
          { label: "BKK Induk", value: UserLevel.BKKInduk },
          { label: "BKK Wilker", value: UserLevel.BKKWilker },
        ];
      case UserLevel.Fasyankes:
        return [{ label: "Fasyankes", value: UserLevel.Fasyankes }];
      case UserLevel.District:
        return [
          { label: "Kabupaten/Kota", value: UserLevel.District },
          { label: "Fasyankes", value: UserLevel.Fasyankes },
          { label: "BKK Induk", value: UserLevel.BKKInduk },
          { label: "BKK Wilker", value: UserLevel.BKKWilker },
        ];
      case UserLevel.Province:
        return [
          { label: "Provinsi", value: UserLevel.Province },
          { label: "Kabupaten/Kota", value: UserLevel.District },
          { label: "Fasyankes", value: UserLevel.Fasyankes },
          { label: "BKK Induk", value: UserLevel.BKKInduk },
          { label: "BKK Wilker", value: UserLevel.BKKWilker },
        ];
      default:
        return [
          { label: "Nasional", value: UserLevel.National },
          { label: "Provinsi", value: UserLevel.Province },
          { label: "Kabupaten/Kota", value: UserLevel.District },
          { label: "Fasyankes", value: UserLevel.Fasyankes },
          { label: "BKK Induk", value: UserLevel.BKKInduk },
          { label: "BKK Wilker", value: UserLevel.BKKWilker },
        ];
    }
  };

  const fetchBKKInduk = async () => {
    try {
      const response = await BKKService.getAll({
        page: 1,
        limit: 9999,
        province_id:
          selectedProvince?.split("|")[0] ||
          (currentUser?.level === UserLevel.Province
            ? currentUser.province_id
            : ""),
        district_id:
          selectedDistrict?.split("|")[0] ||
          (currentUser?.level === UserLevel.District
            ? currentUser.district_id
            : ""),
      });
      const bkkList = response.bkk || [];
      const induks = bkkList.filter((item) => !item.induk_id);
      setLevelDataOptions((prevState) => ({
        ...prevState,
        induks: induks.map((item) => ({
          label: item.name,
          value: `${item.id}|${item.name}`,
        })),
      }));
    } catch (error) {
      console.log(error);
    }
  };

  const fetchBKKWilker = async (indukId) => {
    try {
      const params = {
        page: 1,
        limit: 9999,
        province_id:
          selectedProvince?.split("|")[0] ||
          (currentUser?.level === UserLevel.Province
            ? currentUser.province_id
            : ""),
        district_id:
          selectedDistrict?.split("|")[0] ||
          (currentUser?.level === UserLevel.District
            ? currentUser.district_id
            : ""),
      };
      if (indukId) {
        params.searchBy = "induk_id";
        params.searchValue = indukId;
      }
      const response = await BKKService.getAll(params);
      const bkkList = response.bkk || [];
      const wilkers = bkkList.filter((item) => item.induk_id);
      setLevelDataOptions((prevState) => ({
        ...prevState,
        wilkers: wilkers.map((item) => ({
          label: item.name,
          value: `${item.id}|${item.name}`,
        })),
      }));
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (
      selectedLevel === UserLevel.BKKInduk ||
      selectedLevel === UserLevel.BKKWilker
    ) {
      fetchBKKInduk();
    }
    if (selectedLevel === UserLevel.BKKWilker) {
      fetchBKKWilker();
    }
  }, [selectedLevel, selectedProvince, selectedDistrict]);

  useEffect(() => {
    if (selectedInduk) {
      fetchBKKWilker(selectedInduk.split("|")[0]);
    }
  }, [selectedInduk]);

  const fetchAllProvincies = async () => {
    try {
      const response = await WilayahService.getAll(Source.PROVINCE, {
        page: 1,
        limit: 9999,
      });
      setLevelDataOptions((prev) => ({
        ...prev,
        provinces: response.province.map((item) => ({
          label: item.name,
          value: `${item.id}|${item.name}`,
        })),
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const fetchDistrictByProvince = async (provinceId) => {
    try {
      const response = await WilayahService.getAll(Source.DISTRICT, {
        page: 1,
        limit: 9999,
        searchBy: "province_id",
        searchValue: provinceId,
      });
      setLevelDataOptions((prev) => ({
        ...prev,
        districts: response.district.map((item) => ({
          label: item.name,
          value: `${item.id}|${item.name}`,
        })),
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const fetchFasyankesByDistrict = async (districtId) => {
    try {
      const response = await FasyankesService.getAll(Source.FASYANKES, {
        page: 1,
        limit: 9999,
        searchBy: "district_id",
        searchValue: districtId,
      });
      setLevelDataOptions((prev) => ({
        ...prev,
        fasyankes: response.fasyankes.map((item) => ({
          label: item.name,
          value: `${item.id}|${item.name}`,
        })),
      }));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchAllProvincies();
    if (selectedProvince)
      fetchDistrictByProvince(selectedProvince?.split("|")[0]);
    if (selectedDistrict)
      fetchFasyankesByDistrict(selectedDistrict?.split("|")[0]);
  }, [selectedProvince, selectedDistrict]);

  useEffect(() => {
    setLevelData(generateLevelOptions(currentUser?.level));
    if (currentUser.level === UserLevel.Fasyankes) {
      setLevelIdValue(currentUser?.fasyankes_id);
    } else if (currentUser.level === UserLevel.BKKWilker) {
      setLevelIdValue(`${currentUser.username}|${currentUser.first_name}`);
      BKKService.getIndukByWilker(currentUser.username).then((getBkk) => {
        setSelectedInduk(`${getBkk.induk_id}|${getBkk.induk_name}`);
        setSelectedWilker(`${currentUser.username}|${currentUser.first_name}`);
      });
    } else if (currentUser.level === UserLevel.BKKInduk) {
      setLevelIdValue(`${currentUser.username}|${currentUser.first_name}`);
      setSelectedInduk(`${currentUser.username}|${currentUser.first_name}`);
    } else if (currentUser.level === UserLevel.District) {
      setLevelIdValue(currentUser?.district_id);
    } else if (currentUser.level === UserLevel.Province) {
      setLevelIdValue(currentUser?.province_id);
    } else {
      setLevelIdValue("");
    }
  }, [currentUser]);

  useEffect(() => {
    if (selectedLevel !== UserLevel.BKKInduk) return;

    if (currentUser.level === UserLevel.BKKInduk) {
      const defaultInduk = getBkkUserValue(currentUser);
      if (!selectedInduk || !selectedInduk.includes("|")) {
        setSelectedInduk(defaultInduk);
        setLevelIdValue(defaultInduk);
        return;
      }
    }

    if (selectedInduk) {
      setLevelIdValue(selectedInduk);
    }
  }, [selectedLevel, currentUser]);

  const handleApplyFilter = async () => {
    setApplySelectedLevel(selectedLevel);
    setIsLoading(true);
    setShowData(false);

    const params = {
      level: selectedLevel,
      level_id: resolveLevelIdForApply({
        selectedLevel,
        levelIdValue,
        selectedInduk,
        selectedWilker,
      }),
    };

    try {
      const response = await LaporanService.MicroscopistFaskes(params);
      setReportInfo({
        statusPeriode: periodeLaporan,
        periode: periodeLaporanValue,
        province: selectedProvince,
        district: selectedDistrict,
        fasyankes: selectedFasyankes,
        induk: selectedInduk,
        wilker: selectedWilker,
      });
      setReportData(response);
      setCurrentLevel(params.level);
    } catch (error) {
      setReportData([]);
    } finally {
      setIsLoading(false);
      setShowData(true);
    }
  };

  return (
    <div className="dashboard-wrapper">
      <BreadcrumbNav
        items={[
          { label: "Laporan", onClick: () => history.push("/laporan") },
          { label: module },
        ]}
      />
      <FilterSection
        currentUser={currentUser}
        periodeLaporan={periodeLaporan}
        setPeriodeLaporan={setPeriodeLaporan}
        periodeLaporanValue={periodeLaporanValue}
        setPeriodeLaporanValue={setPeriodeLaporanValue}
        selectedLevel={selectedLevel}
        setSelectedLevel={setSelectedLevel}
        levelData={levelData}
        levelDataOptions={levelDataOptions}
        setLevelIdValue={setLevelIdValue}
        selectedProvince={selectedProvince}
        setSelectedProvince={setSelectedProvince}
        selectedDistrict={selectedDistrict}
        setSelectedDistrict={setSelectedDistrict}
        selectedFasyankes={selectedFasyankes}
        selectedInduk={selectedInduk}
        setSelectedInduk={setSelectedInduk}
        selectedWilker={selectedWilker}
        setSelectedWilker={setSelectedWilker}
        setSelectedFasyankes={setSelectedFasyankes}
        handleApplyFilter={handleApplyFilter}
        tableRef={tableRef}
        showData={showData}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        levelIdValue={levelIdValue}
        reportName="LAPORAN_MIKROSKOPIS_FASYANKES_BKK"
      />
      {isLoading && !showData && <LoadingSpinner />}
      {showData && (
        <div className="table-container">
          <div className="data-title">
            <h2>{module}</h2>
          </div>
          <div className="data-main">
            <TableComponent
              currentLevel={applySelectedLevel}
              reportsData={reportData}
              tableRef={tableRef}
              reportInfo={reportInfo}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DetailLaporan;
